import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Typography, Box } from "@mui/material";
import I18n from "i18next";

import { MzTabContext, MzTabPanel } from "@markaaz/ui/MzTabControls";

import {
  loadDecisioningValues,
  updateDecisioningValues,
  submitDecisioningValues,
  loadNameMatchingValues,
  selectNameMatchingValues,
  submitNameMatchingValues,
  updateNameMatchingValues
} from "store/slices/onboardingPage";
import { selectDecisioningValues } from "store/slices/onboardingPage";

import DecisioningMain from "../components/application/decisioning/DecisioningMain";
import NameMatchingMain from "../components/application/nameMatching/NameMatchingMain";
import decisioningData from "../data/decisioning-data.json";
import nameMatchingData from "../data/nameMatching-data.json";
import { loadingPage, selectPartnersData, selectUser } from "store/slices/app";

const sx = {
  heading: {
    color: "darkBlue.c100",
    marginBottom: "40px"
  },
  headerContent: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center"
  }
};

const ApplicationOnboarding = () => {
  const { tab } = useParams();
  const dispatch = useDispatch();
  const decisioningValues = useSelector(selectDecisioningValues);
  const nameMatchingValues = useSelector(selectNameMatchingValues);
  const partnersData = useSelector(selectPartnersData);
  const userData = useSelector(selectUser);

  const [decisioningSubmitInProgress, setDecisioningSubmitInProgress] = useState(false);
  const [nameMatchingSubmitInProgress, setNameMatchingSubmitInProgress] = useState(false);
  const [showDecisionNotification, setShowDecisionNotification] = useState({ type: "", message: "" });
  const [ShowNameMatchNotification, setShowNameMatchNotification] = useState({ type: "", message: "" });
  const loadData = (userData.isSuperUser && partnersData.isPartnerChanged) || !userData.isSuperUser;

  useEffect(() => {
    setShowDecisionNotification(false);
    dispatch(loadDecisioningValues(userData.selectedPartner.id));
  }, [dispatch, userData.selectedPartner.id]);

  // useEffect(() => {
  //   dispatch(loadVerificationData(userData.selectedPartner.id));
  // }, [dispatch, userData.selectedPartner.id]);

  useEffect(() => {
    setShowNameMatchNotification(false);
    dispatch(loadNameMatchingValues(userData.selectedPartner.id));
  }, [dispatch, userData.selectedPartner.id]);

  async function handleDecisioningSubmit() {
    dispatch(loadingPage(true));
    setDecisioningSubmitInProgress(true);
    setShowDecisionNotification(false);
    const { approvalStatus, approvedOn, isPremium, ...newDecisioningValues } = decisioningValues;

    try {
      await new Promise((resolve) => setTimeout(resolve, 1000));
      await dispatch(submitDecisioningValues(newDecisioningValues));

      setShowDecisionNotification({
        type: "success",
        message: "Successfully Updated"
      });
    } catch (error) {
      setDecisioningSubmitInProgress(false);
      setShowDecisionNotification({
        type: "error",
        message: "Something went wrong. Please try again later."
      });
    } finally {
      setDecisioningSubmitInProgress(false);
      dispatch(loadingPage(false));
    }
  }

  async function handleNameMatchingSubmit() {
    dispatch(loadingPage(true));
    setNameMatchingSubmitInProgress(true);
    setShowNameMatchNotification(false);
    try {
      await new Promise((resolve) => setTimeout(resolve, 1000));
      await dispatch(submitNameMatchingValues(nameMatchingValues));
      setShowNameMatchNotification({
        type: "success",
        message: "Successfully Updated"
      });
    } catch (error) {
      setShowNameMatchNotification({
        type: "error",
        message: "Something went wrong. Please try again later."
      });
    } finally {
      setNameMatchingSubmitInProgress(false);
      dispatch(loadingPage(false));
    }
  }

  function handleNameMatchingSliderChange(rowData) {
    return function (_, value) {
      if (value[0] !== value[1]) {
        let changedValue = {};
        if (Array.isArray(rowData.slider.valueKey)) {
          changedValue[rowData.slider.valueKey[0]] = value[0];
          changedValue[rowData.slider.valueKey[1]] = value[1];
        } else {
          changedValue[rowData.slider.valueKey] = value;
        }

        dispatch(updateNameMatchingValues(changedValue));
      }
    };
  }

  function handleRadioChange(rowData) {
    return function (_, rowValue) {
      const radioKey = rowData.radioKey;
      if (!radioKey) return;

      const changedValue = {};
      changedValue[radioKey] = rowValue;
      dispatch(updateDecisioningValues(changedValue));
    };
  }

  function handleCheckboxChange(rowData) {
    return function (_, checked) {
      const changedValue = { [rowData.checkboxKey]: checked };
      dispatch(updateDecisioningValues(changedValue));
    };
  }

  function handleSliderChange(rowData) {
    return function (_, value) {
      const checked = decisioningValues[rowData.checkboxKey];
      if (!checked) return;
      let changedValue = {};
      if (Array.isArray(rowData.slider.valueKey)) {
        if (value[0] === value[1]) return;
        changedValue[rowData.slider.valueKey[0]] = value[0];
        changedValue[rowData.slider.valueKey[1]] = value[1];
      } else {
        changedValue[rowData.slider.valueKey] = value;
      }

      dispatch(updateDecisioningValues(changedValue));
    };
  }

  async function handleDecisioningReset() {
    setTimeout(function () {
      dispatch(loadDecisioningValues(userData.selectedPartner.id));
    }, 1000);
  }

  async function handleNameMatchingReset() {
    setTimeout(function () {
      dispatch(loadNameMatchingValues(userData.selectedPartner.id));
    }, 1000);
  }

  async function handleAlertClose(name) {
    switch (name) {
      case "dc":
        setShowDecisionNotification({ type: "", message: "" });
        break;
      case "nm":
        setShowNameMatchNotification({ type: "", message: "" });
        break;

      default:
        break;
    }
  }

  return (
    <Box>
      <Box sx={sx.headerContent}>
        <Typography variant="h4" sx={sx.heading}>
          {tab === "decisioning"
            ? I18n.t("applicationOnboarding.decisioning")
            : I18n.t("applicationOnboarding.p2bNameMatching")}
        </Typography>
      </Box>

      <MzTabContext value={tab}>
        <MzTabPanel value="decisioning">
          <DecisioningMain
            values={decisioningValues}
            data={decisioningData}
            submitInProgress={decisioningSubmitInProgress}
            showDecisionNotification={showDecisionNotification}
            onRadioChange={handleRadioChange}
            onCheckboxChange={handleCheckboxChange}
            onSliderChange={handleSliderChange}
            onSubmit={handleDecisioningSubmit}
            onReset={handleDecisioningReset}
            onAlertClose={handleAlertClose}
            isPartnerChanged={!loadData}
          />
        </MzTabPanel>
        <MzTabPanel value="p2b">
          <NameMatchingMain
            values={nameMatchingValues}
            data={nameMatchingData}
            submitInProgress={nameMatchingSubmitInProgress}
            ShowNameMatchNotification={ShowNameMatchNotification}
            onSliderChange={handleNameMatchingSliderChange}
            onSubmit={handleNameMatchingSubmit}
            onAlertClose={handleAlertClose}
            onReset={handleNameMatchingReset}
            isPartnerChanged={!loadData}
          />
        </MzTabPanel>
      </MzTabContext>
    </Box>
  );
};

export default ApplicationOnboarding;
